import { Select, SelectContent } from '@dextech-ai/dex-design-system';
import { renderLabelOptions } from '@/utils/select-items';
import PropTypes from 'prop-types';
import { useEffect, useMemo, useRef, useState } from 'react';

import FileUploadOptionsForm from './FileUploadOptionsForm';
import OptionListEditor from './OptionListEditor';

/**
 * Renderer Hints editor — replaces the raw JSON textarea for x-uioptions
 * with structured per-uitype editors.
 *
 * Per-uitype shape conventions (from production audit; see
 * src/hooks/use-data-schema-versioning/index.js:124-131):
 *   - dropdown:        JSON-encoded STRING of [{value, label}, ...]
 *   - radio:           native ARRAY of [{value, label}, ...]
 *   - multi-select:    native ARRAY of [{value, label}, ...]
 *   - file-upload:     native OBJECT {minItems, maxItems, allowedExtensions, allowedSizePerFileInMB}
 *   - dynamic-select:  JSON-encoded STRING, opaque shape — kept as raw advanced
 *   - textarea / '':   no options
 *
 * The editor exposes these to onChange in the SAME serialization shape as
 * the input value, so byte-for-byte round-trip survives in __roundtrip__/gapD.
 *
 * Escape hatch: a small "Advanced" toggle drops back into the raw-JSON
 * textarea for any uitype. We auto-open it when an existing value can't
 * be coerced into the expected shape (e.g. a malformed JSON string on a
 * dropdown), so nothing is dropped silently.
 */

// uitype groups
const OPTION_LIST_UITYPES_NATIVE = new Set(['radio', 'multi-select']);
const OPTION_LIST_UITYPES_STRING = new Set(['dropdown']);
const OPTION_LIST_UITYPES = new Set([
	...OPTION_LIST_UITYPES_NATIVE,
	...OPTION_LIST_UITYPES_STRING,
]);
const FILE_UPLOAD_UITYPE = 'file-upload';
const STRING_OPAQUE_UITYPES = new Set(['dynamic-select']);
const NO_OPTIONS_UITYPES = new Set(['', 'textarea']);

const X_UITYPE_OPTIONS = [
	{ value: '', label: '— none —' },
	{ value: 'dropdown', label: 'dropdown' },
	{ value: 'radio', label: 'radio' },
	{ value: 'multi-select', label: 'multi-select' },
	{ value: 'file-upload', label: 'file-upload' },
	{ value: 'dynamic-select', label: 'dynamic-select' },
	{ value: 'textarea', label: 'textarea' },
];

function valueToText(v) {
	if (v === undefined) return '';
	if (typeof v === 'string') return v;
	try {
		return JSON.stringify(v, null, 2);
	} catch (_e) {
		return '';
	}
}

// For option-list uitypes, decode the value into a native array of
// {value, label} rows. dropdown stores it as a JSON-encoded string;
// radio/multi-select store it natively. Returns { ok, array } so the
// orchestrator can fall back to advanced mode when the shape isn't
// understandable.
function decodeOptionList(uitype, value) {
	if (value === undefined || value === null || value === '') {
		return { ok: true, array: [] };
	}
	let candidate = value;
	if (OPTION_LIST_UITYPES_STRING.has(uitype) && typeof value === 'string') {
		try {
			candidate = JSON.parse(value);
		} catch (_e) {
			return { ok: false, array: [] };
		}
	}
	if (!Array.isArray(candidate)) {
		return { ok: false, array: [] };
	}
	const allObjectShape = candidate.every((entry) => entry !== null && typeof entry === 'object' && !Array.isArray(entry));
	return { ok: allObjectShape, array: candidate };
}

function decodeFileUpload(value) {
	if (value === undefined || value === null) {
		return { ok: true, object: {} };
	}
	if (typeof value === 'object' && !Array.isArray(value)) {
		return { ok: true, object: value };
	}
	return { ok: false, object: {} };
}

// Encode an option-list array back to the shape the schema expects for
// this uitype: JSON-encoded string for `dropdown`, native array for
// `radio` / `multi-select`. An empty array becomes undefined so the
// emitter strips x-uioptions cleanly.
function encodeOptionList(uitype, array) {
	if (!Array.isArray(array) || array.length === 0) return undefined;
	if (OPTION_LIST_UITYPES_STRING.has(uitype)) {
		return JSON.stringify(array);
	}
	return array;
}

// Try to parse the advanced-textarea content into the shape the uitype
// expects. We mirror the historical JSON-string fallback in
// dsv-update-schema.jsx: parse if we can, otherwise keep the raw string.
function parseAdvancedText(text) {
	const trimmed = (text || '').trim();
	if (trimmed === '') return undefined;
	try {
		return JSON.parse(trimmed);
	} catch (_e) {
		return text;
	}
}

export default function RenderHintsForm({ xUitype, xUioptions, onChange, disabled = false }) {
	// Which mode the editor is in for the current uitype:
	//   - 'structured' uses the per-uitype sub-editor
	//   - 'advanced'   shows the raw JSON textarea (escape hatch)
	// We track per-uitype so toggling uitype back resumes its mode.
	const [advancedByUitype, setAdvancedByUitype] = useState({});
	const [advancedText, setAdvancedText] = useState(() => valueToText(xUioptions));

	// Detect "this value can't be represented in the structured editor"
	// once on mount and on uitype change, and force-advanced for it so
	// the user sees the raw value instead of an empty editor.
	const wasAutoForcedRef = useRef(new Set());

	useEffect(() => {
		if (NO_OPTIONS_UITYPES.has(xUitype) || STRING_OPAQUE_UITYPES.has(xUitype)) return;
		if (wasAutoForcedRef.current.has(xUitype)) return;

		let supported = true;
		if (OPTION_LIST_UITYPES.has(xUitype)) {
			supported = decodeOptionList(xUitype, xUioptions).ok;
		} else if (xUitype === FILE_UPLOAD_UITYPE) {
			supported = decodeFileUpload(xUioptions).ok;
		}
		if (!supported) {
			wasAutoForcedRef.current.add(xUitype);
			setAdvancedByUitype((prev) => ({ ...prev, [xUitype]: true }));
		}
	}, [xUitype, xUioptions]);

	// Keep the advanced textarea in sync with the typed value when we are
	// NOT in advanced mode (so flipping into advanced mid-edit shows the
	// current shape) and when the value is replaced from outside.
	useEffect(() => {
		const inAdvanced = !!advancedByUitype[xUitype] || STRING_OPAQUE_UITYPES.has(xUitype);
		if (inAdvanced) return;
		setAdvancedText(valueToText(xUioptions));
	}, [xUioptions, xUitype, advancedByUitype]);

	const isAdvanced = !!advancedByUitype[xUitype] || STRING_OPAQUE_UITYPES.has(xUitype);
	const showOptions = !NO_OPTIONS_UITYPES.has(xUitype);

	const sectionOpen = useMemo(
		() => !!(xUitype || xUioptions !== undefined),
		[xUitype, xUioptions],
	);

	const handleUitypeChange = (next) => {
		// Switching uitype clears the auto-force memory for the new
		// uitype so its "is this value supported?" check runs fresh.
		wasAutoForcedRef.current.delete(next);

		// Re-shape options when crossing the native↔string convention
		// boundary, so dropdown↔multi-select keeps the user's row data.
		const fromNative = OPTION_LIST_UITYPES_NATIVE.has(xUitype);
		const fromString = OPTION_LIST_UITYPES_STRING.has(xUitype);
		const toNative = OPTION_LIST_UITYPES_NATIVE.has(next);
		const toString = OPTION_LIST_UITYPES_STRING.has(next);

		let nextOptions = xUioptions;
		if (NO_OPTIONS_UITYPES.has(next)) {
			nextOptions = undefined;
		} else if (fromString && toNative) {
			const { ok, array } = decodeOptionList(xUitype, xUioptions);
			nextOptions = ok ? array : xUioptions;
		} else if (fromNative && toString) {
			if (Array.isArray(xUioptions)) {
				nextOptions = JSON.stringify(xUioptions);
			}
		}
		onChange({ xUitype: next, xUioptions: nextOptions });
	};

	const handleStructuredOptionListChange = (nextArray) => {
		const encoded = encodeOptionList(xUitype, nextArray);
		onChange({ xUitype, xUioptions: encoded });
	};

	const handleStructuredFileUploadChange = (nextObject) => {
		onChange({ xUitype, xUioptions: nextObject ?? undefined });
	};

	const handleAdvancedTextChange = (text) => {
		setAdvancedText(text);
		const parsed = parseAdvancedText(text);
		onChange({ xUitype, xUioptions: parsed });
	};

	const toggleAdvanced = () => {
		const next = !isAdvanced;
		setAdvancedByUitype((prev) => ({ ...prev, [xUitype]: next }));
		if (next) {
			// Entering advanced — preload the textarea with the current
			// typed value so the user sees what they're about to edit.
			setAdvancedText(valueToText(xUioptions));
		}
	};

	const optionList = OPTION_LIST_UITYPES.has(xUitype)
		? decodeOptionList(xUitype, xUioptions).array
		: [];
	const fileUploadObject = xUitype === FILE_UPLOAD_UITYPE
		? decodeFileUpload(xUioptions).object
		: {};

	const advancedToggleLabel = isAdvanced ? 'Use guided editor' : 'Advanced (raw JSON)';
	const isAdvancedLocked = STRING_OPAQUE_UITYPES.has(xUitype);

	return (
		<details
			className="dsv-modal__constraints-section"
			open={sectionOpen}
			data-testid="render-hints-section"
		>
			<summary className="dsv-modal__constraints-toggle">Renderer Hints</summary>
			<div className="dsv-modal__constraints-body">
				<div className="dsv-modal__field">
					<label className="dsv-modal__label" htmlFor="dsv-field-x-uitype">UI Type</label>
					<Select
						id="dsv-field-x-uitype"
						value={xUitype || ''}
						disabled={disabled}
						onChange={handleUitypeChange}
						data-testid="render-hints-uitype"
					>
						<SelectContent>{renderLabelOptions(X_UITYPE_OPTIONS)}</SelectContent>
					</Select>
					<p className="dsv-promote__hint" style={{ marginTop: 4 }}>
						Controls how this field is rendered in the runtime form.
					</p>
				</div>

				{showOptions && (
					<div className="dsv-modal__field">
						<div className="dsv-render-hints__options-header">
							<label className="dsv-modal__label">UI Options</label>
							{!isAdvancedLocked && (
								<button
									type="button"
									className="dsv-render-hints__advanced-toggle"
									onClick={toggleAdvanced}
									disabled={disabled}
									data-testid="render-hints-advanced-toggle"
								>
									{advancedToggleLabel}
								</button>
							)}
						</div>

						{!isAdvanced && OPTION_LIST_UITYPES.has(xUitype) && (
							<OptionListEditor
								value={optionList}
								disabled={disabled}
								onChange={handleStructuredOptionListChange}
							/>
						)}

						{!isAdvanced && xUitype === FILE_UPLOAD_UITYPE && (
							<FileUploadOptionsForm
								value={fileUploadObject}
								disabled={disabled}
								onChange={handleStructuredFileUploadChange}
							/>
						)}

						{isAdvanced && (
							<>
								<textarea
									className="dsv-modal__input"
									rows={5}
									value={advancedText}
									disabled={disabled}
									onChange={(e) => handleAdvancedTextChange(e.target.value)}
									placeholder='— optional JSON, leave blank for none —'
									data-testid="render-hints-advanced-textarea"
								/>
								<p className="dsv-promote__hint" style={{ marginTop: 4 }}>
									{isAdvancedLocked
										? 'This UI Type stores its options as a free-form value. Edit the raw JSON above.'
										: 'Free-form JSON. If your input isn’t valid JSON it round-trips as a raw string.'}
								</p>
							</>
						)}
					</div>
				)}
			</div>
		</details>
	);
}

RenderHintsForm.propTypes = {
	xUitype: PropTypes.string,
	// xUioptions is intentionally polymorphic: string for dropdown /
	// dynamic-select, array for radio / multi-select, object for
	// file-upload, undefined when absent.
	// eslint-disable-next-line react/forbid-prop-types
	xUioptions: PropTypes.any,
	onChange: PropTypes.func.isRequired,
	disabled: PropTypes.bool,
};
